import { Actions, ofType, createEffect } from '@ngrx/effects';
import { map, switchMap, catchError, tap, mergeMap, exhaustMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Injectable, Inject } from '@angular/core';
import { EMPTY, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';

import * as AuthActions from '../../../core/store/actions/auth.actions';
import * as fromApp from '../app.state';
import * as fromAuth from '../reducers/auth.reducers';
import * as storageHelper from '../../helpers/storage-helper'
import { User } from '../../../shared/interfaces/user.type';

import { LoggedInModuleType } from '../../../shared/interfaces/logged-in-module.type';
import { LoginResponse, LoginResponseWithModule } from '../../../shared/interfaces/login-response.type';
import { AuthService } from '../../../../services/auth.service';
import { ChooseLoginModuleDialogComponent } from '../../components/choose-login-module-dialog/choose-login-module-dialog.component';
import { HttpErrorResponse } from '@angular/common/http';
import { HelperService } from '@app/services/helper.service';
import { LoggingService, LogLevel } from '@app/services/logging.service';
import { login } from '../../../core/store/actions/auth.actions';
import { InputOtpDialogComponent } from '../../components/input-otp-dialog/input-otp-dialog.component';


@Injectable()
export class AuthEffects {
    static readonly COMPONENT_NAME = 'AuthEffects';


    constructor(
        private actions$: Actions,
        private authService: AuthService,
        private store: Store<fromApp.AppState>,
        private router: Router,
        public dialog: MatDialog,
        private helper: HelperService,
        private loggingService: LoggingService
    ) { }

    login$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.login),
            switchMap(payload => this.authService.login(payload.email, payload.password).pipe(
                map((loginResponse: LoginResponse) => {

                    this.loggingService.log(LogLevel.DEBUG, AuthEffects.COMPONENT_NAME, 'Login Response:', loginResponse);
                    const rememberMe = payload.rememberMe;
                    if (loginResponse.twoFactorAuth && loginResponse.twoFactorAuthToken) {
                        return AuthActions.twoFactorAuthRequired({ user: loginResponse.user, twoFactorAuthToken: loginResponse.twoFactorAuthToken, rememberMe });
                    }

                    // Check if user is engineer AND (orgUser OR orgAdmin) - need to choose module
                    const isEngineerAndOrgRole = loginResponse.user.isEngineer &&
                        (loginResponse.user.isOrgUser || loginResponse.user.isOrgAdmin);

                    if (loginResponse.roles && loginResponse.roles.length > 1) {
                        return AuthActions.loginAreaChooseDialog({ ...loginResponse, rememberMe });
                    } else if (isEngineerAndOrgRole) {
                        // User has engineer role along with org role, show dialog to choose
                        return AuthActions.loginAreaChooseDialog({ ...loginResponse, rememberMe });
                    } else {
                        let loggedInModule: LoggedInModuleType;
                        loggedInModule = loginResponse.roles[0];    // When there is only one role, just choose that....
                        return AuthActions.loginSuccess({ ...loginResponse, loggedInModule, rememberMe });
                    }
                }),
                catchError((err: HttpErrorResponse) => {
                    this.loggingService.log(LogLevel.ERROR, AuthEffects.COMPONENT_NAME, 'Login Error:', err);
                    return of(AuthActions.loginFailure({ error: err }))
                })
            ))
        )
    );

    loginSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.loginSuccess),
            tap((resp: LoginResponseWithModule & { rememberMe: boolean }) => {
                console.warn('[AuthEffect] loginSuccess$: payload', resp);
                console.warn('[DIAG] loginSuccess$ fired — stack trace:', new Error().stack);

                // "Remember Me" checked → localStorage (survives browser restart).
                // Unchecked → sessionStorage (cleared when the last tab closes).
                // Token TTLs are derived from the JWTs' own `exp` claims so the
                // client never expires a token earlier than the server would.
                const persist: storageHelper.PersistMode = resp.rememberMe ? 'local' : 'session';
                const accessTtl = storageHelper.jwtRemainingMs(resp.accessToken);
                const refreshTtl = storageHelper.jwtRemainingMs(resp.refreshToken);
                // For user/module data we don't have a JWT exp — fall back to the
                // refresh token's lifetime so they invalidate together.
                storageHelper.setItemWithExpiry('accessToken', resp.accessToken, accessTtl, persist);
                storageHelper.setItemWithExpiry('refreshToken', resp.refreshToken, refreshTtl, persist);
                storageHelper.setItemWithExpiry('user', resp.user, refreshTtl, persist);
                storageHelper.setItemWithExpiry('loggedInModule', resp.loggedInModule, refreshTtl, persist);

                let redirectUrl: string | null = new URL(window.location.href).searchParams.get('redirectUrl');

                if (redirectUrl && redirectUrl != 'null' && redirectUrl != '' && redirectUrl != '/login') {
                    redirectUrl = decodeURI(redirectUrl);
                    console.warn('[AuthEffect] loginSuccess$: redirectUrl:', redirectUrl);
                    this.router.navigateByUrl(redirectUrl);
                } else {
                    console.warn('[AuthEffect] loginSuccess$: no redirectUrl, navigating to dashboard');
                    switch (resp.loggedInModule) {
                        case 'customer':
                            this.router.navigate(['/tenant/dashboard']);
                            break;
                        case 'engineer':
                            this.router.navigate(['/engineer/dashboard']);
                            break;
                        case 'orgAdmin':
                        case 'orgUser':
                            this.router.navigate(['/admin/dashboard']);
                            break;
                        case 'superAdmin':
                            this.router.navigate(['/superadmin/organisations']);
                            break;
                    }
                }
            }),
        ),
        { dispatch: false }
    );


    twoFactorAuthRequired$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.twoFactorAuthRequired),
            exhaustMap(({ user, twoFactorAuthToken, rememberMe }) => {
                const dialogRef = this.dialog.open(InputOtpDialogComponent, {
                    width: "500px",
                    maxWidth: '95vw',
                    panelClass: ['tis-simple-confirmation'],
                    data: {}
                });

                this.loggingService.log(LogLevel.DEBUG, AuthEffects.COMPONENT_NAME, 'user:', user);
                this.loggingService.log(LogLevel.DEBUG, AuthEffects.COMPONENT_NAME, 'twoFactorAuthToken:', twoFactorAuthToken);

                return dialogRef.afterClosed().pipe(
                    mergeMap(result => {
                        this.loggingService.log(LogLevel.DEBUG, AuthEffects.COMPONENT_NAME, 'loginAreaChooseDialog dialog closed, result:', result);

                        return this.authService.verifyTwoFactorAuthOtp(user.id, result.otp, twoFactorAuthToken).pipe(
                            map((loginResponse: LoginResponse) => {
                                this.loggingService.log(LogLevel.DEBUG, AuthEffects.COMPONENT_NAME, 'Two Factor Auth Response:', loginResponse);

                                // Check if user is engineer AND (orgUser OR orgAdmin) - need to choose module
                                const isEngineerAndOrgRole = loginResponse.user.isEngineer && 
                                    (loginResponse.user.isOrgUser || loginResponse.user.isOrgAdmin);

                                if (loginResponse.roles && loginResponse.roles.length > 1) {
                                    return AuthActions.loginAreaChooseDialog({ ...loginResponse, rememberMe });
                                } else if (isEngineerAndOrgRole) {
                                    // User has engineer role along with org role, show dialog to choose
                                    return AuthActions.loginAreaChooseDialog({ ...loginResponse, rememberMe });
                                } else {
                                    let loggedInModule: LoggedInModuleType;
                                    loggedInModule = loginResponse.roles[0];
                                    return AuthActions.loginSuccess({ ...loginResponse, loggedInModule, rememberMe });
                                }
                            }),
                            catchError((err: HttpErrorResponse) => {
                                this.loggingService.log(LogLevel.ERROR, AuthEffects.COMPONENT_NAME, 'Two Factor Auth Error:', err);
                                this.helper.showHttpErrorMsg(err, 5500);
                                return of(AuthActions.loginFailure({ error: err }));
                            })
                        );
                    })
                );
            })
        ),
    );


    loginAreaChooseDialog$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.loginAreaChooseDialog),
            exhaustMap((payload) => {
                const { rememberMe, ...loginResponse } = payload;

                const dialogRef = this.dialog.open(ChooseLoginModuleDialogComponent, {
                    width: "500px",
                    maxWidth: '95vw',
                    panelClass: ['tis-simple-confirmation'],
                    data: {}
                });

                // Use the exhaustMap operator to wait for the dialog to be closed
                const modifiedResponse: LoginResponse = JSON.parse(JSON.stringify(loginResponse));
                this.loggingService.log(LogLevel.DEBUG, AuthEffects.COMPONENT_NAME, 'Line Login Response:', modifiedResponse);

                return dialogRef.afterClosed().pipe(
                    map(result => {
                        this.loggingService.log(LogLevel.DEBUG, AuthEffects.COMPONENT_NAME, 'loginAreaChooseDialog dialog closed, result:', result);

                        let loggedInModule: LoggedInModuleType | undefined;
                        if (result === 1) {
                            // Choose admin/user role (filter out 'engineer' role)
                            loggedInModule = loginResponse.roles.filter(r => r != 'engineer')[0];
                        } else if (result === 2) {
                            // Choose engineer role
                            loggedInModule = 'engineer';
                        }

                        if (loggedInModule) {
                            return AuthActions.loginSuccess({ ...modifiedResponse, loggedInModule, rememberMe });
                        } else {
                            this.helper.showErrorMsg('You have not selected any module. Please try again!', 'Error', 3500);
                            return AuthActions.noAction();
                        }
                    })
                );
            })
        ),
    );


    updateUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.updateUser),
            tap((userObj: { user: User }) => {
                console.warn('[AuthEffect] updateUser$: payload:', userObj);
                // Write back to whichever storage currently holds the user; fall
                // back to the refresh token's storage if user was somehow missing.
                const persist = storageHelper.getPersistModeForKey('user')
                    ?? storageHelper.getPersistModeForKey('refreshToken')
                    ?? 'local';
                const ttl = storageHelper.jwtRemainingMs(storageHelper.getItemWithExpiry('refreshToken'))
                    || (168 * 60 * 60 * 1000); // 7 days fallback if refresh token is unreadable
                storageHelper.setItemWithExpiry('user', userObj.user, ttl, persist);
            }),
        ),
        { dispatch: false }
    );


    refreshTokenInProgress$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.refreshTokenInProgress),
            tap(() => {
                console.warn('[AuthEffect] refreshTokenInProgress$');
                // Don't navigate anywhere...
            }),
        ),
        { dispatch: false }
    );

    refreshTokenSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.refreshTokenSuccess),
            tap((loginResponse: LoginResponse) => {
                console.warn('[AuthEffect] refreshTokenSuccess$: payload', loginResponse);
                // Preserve the user's original "Remember Me" choice by writing
                // the rotated tokens to the same storage that held the previous
                // ones (localStorage → keep using localStorage; sessionStorage
                // → keep using sessionStorage).
                const persist = storageHelper.getPersistModeForKey('refreshToken')
                    ?? storageHelper.getPersistModeForKey('accessToken')
                    ?? 'local';
                const accessTtl = storageHelper.jwtRemainingMs(loginResponse.accessToken);
                const refreshTtl = storageHelper.jwtRemainingMs(loginResponse.refreshToken);
                storageHelper.setItemWithExpiry('accessToken', loginResponse.accessToken, accessTtl, persist);
                storageHelper.setItemWithExpiry('refreshToken', loginResponse.refreshToken, refreshTtl, persist);
                storageHelper.setItemWithExpiry('user', loginResponse.user, refreshTtl, persist);
                // Don't navigate anywhere...
            }),
        ),
        { dispatch: false }
    );

    refreshPermissions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.refreshPermissions),
            switchMap(() => this.authService.getMe().pipe(
                map((res: any) => AuthActions.refreshPermissionsSuccess({ user: res.data.user })),
                catchError((err: HttpErrorResponse) => {
                    // Non-fatal: keep whatever permissions are already in the store
                    // (e.g. from login/localStorage) rather than logging the user out.
                    this.loggingService.log(LogLevel.ERROR, AuthEffects.COMPONENT_NAME, 'Refresh Permissions Error:', err);
                    return of(AuthActions.refreshPermissionsFailure());
                })
            ))
        )
    );

    refreshPermissionsSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.refreshPermissionsSuccess),
            switchMap(() => this.store.select('auth').pipe(take(1))),
            tap((state: fromAuth.State) => {
                if (!state.user) return;
                // Mirrors updateUser$'s persistence pattern — write the merged
                // user back to whichever storage currently holds it.
                const persist = storageHelper.getPersistModeForKey('user')
                    ?? storageHelper.getPersistModeForKey('refreshToken')
                    ?? 'local';
                const ttl = storageHelper.jwtRemainingMs(storageHelper.getItemWithExpiry('refreshToken'))
                    || (168 * 60 * 60 * 1000);
                storageHelper.setItemWithExpiry('user', state.user, ttl, persist);
            }),
        ),
        { dispatch: false }
    );

    unAuthorized$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.unAuthorized),
            tap(() => {
                console.warn('[AuthEffect] unAuthorized$: will redirect to login');
                console.warn('[DIAG] unAuthorized$ fired — stack trace:', new Error().stack);
                // localStorage.clear();
                storageHelper.removeMultipleKeys(['user', 'accessToken', 'refreshToken', 'loggedInModule']);

                let currentUrl = window.location.pathname;
                console.warn('[AuthEffect] unAuthorized$: currentUrl before redirect:', currentUrl);
                currentUrl = encodeURI(currentUrl);
                console.warn('[AuthEffect] unAuthorized$: currentUrl encoded:', currentUrl);

                let loginUrl = '/login';
                if (currentUrl != '/login') {
                    loginUrl = '/login?redirectUrl=' + currentUrl;
                }
                this.router.navigateByUrl(loginUrl);

            }),
        ),
        { dispatch: false }
    );

    logout$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.logout),
            tap((action) => {
                console.warn('[AuthEffect] logout$: will redirect to login');
                console.warn('[DIAG] logout$ fired — manual:', action.manual, '— stack trace:', new Error().stack);

                // Read loggedInModule BEFORE clearing storage so we know
                // whether to preserve redirectUrl (non-customer) or go straight to /login (customer).
                const loggedInModule: LoggedInModuleType = storageHelper.getFromLocalStorageWithExpiry('loggedInModule');

                storageHelper.removeMultipleKeys(['user', 'accessToken', 'refreshToken', 'loggedInModule']);

                if (loggedInModule != 'customer') {
                    let loginUrl = '/login';

                    // Only preserve the current URL when the logout was triggered automatically
                    // (e.g. token refresh failure). Manual logout by the user should always
                    // land on /login without a redirectUrl.
                    if (!action.manual) {
                        const currentUrl = window.location.pathname;
                        if (currentUrl && currentUrl !== '/login') {
                            loginUrl = '/login?redirectUrl=' + encodeURI(currentUrl);
                        }
                    }

                    this.router.navigateByUrl(loginUrl);
                } else {
                    this.router.navigateByUrl('/login');
                }
            }),
        ),
        { dispatch: false }
    );
}
